import { useState, useEffect } from 'react';
import { useTheme } from '../App';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Users, DollarSign, TrendingUp, Gift, ChevronRight, Plus, Send, FileText, Percent } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { affiliateAPI, appealsAPI } from '../lib/api';
import { toast } from 'sonner';

const strings = {
  en: {
    title: 'Affiliate Dashboard', subtitle: 'Earn commissions by referring users to myAct',
    refLink: 'Your Referral Link', copy: 'Copy', copied: 'Copied!',
    stats: 'Your Stats', referredUsers: 'Referred Users', paidAppeals: 'Paid Appeals',
    pendingEarnings: 'Pending Earnings', totalPaid: 'Total Paid Out',
    requestPayout: 'Request Payout', payoutNote: 'Minimum payout amount',
    referredTable: 'Referred Activity', date: 'Date', platform: 'Platform', status: 'Status', commission: 'Commission',
    howItWorks: 'How It Works',
    step1: 'Share your unique referral link',
    step2: 'Users register and submit appeals',
    step3: 'Earn commission on every paid appeal',
    step4: 'Request payout when you reach the minimum',
    noActivity: 'No referred activity yet',
    submitAppeal: 'Submit Appeal', submitTitle: 'Submit Appeal for Client',
    submitDesc: 'Submit an appeal directly on behalf of your referred clients',
    fullName: 'Full Name', email: 'Email', platformLabel: 'Platform', issueType: 'Issue Type',
    description: 'Description', country: 'Country', submit: 'Submit Appeal',
    mySubmissions: 'My Submissions', overview: 'Overview',
    commissionRate: 'Commission Rate',
  },
  ar: {
    title: 'لوحة التسويق بالعمولة', subtitle: 'اكسب عمولات بإحالة المستخدمين إلى myAct',
    refLink: 'رابط الإحالة الخاص بك', copy: 'نسخ', copied: 'تم النسخ!',
    stats: 'إحصائياتك', referredUsers: 'المستخدمون المُحالون', paidAppeals: 'الطعون المدفوعة',
    pendingEarnings: 'الأرباح المعلقة', totalPaid: 'إجمالي المدفوعات',
    requestPayout: 'طلب صرف', payoutNote: 'الحد الأدنى للصرف',
    referredTable: 'نشاط الإحالة', date: 'التاريخ', platform: 'المنصة', status: 'الحالة', commission: 'العمولة',
    howItWorks: 'كيف يعمل',
    step1: 'شارك رابط الإحالة الفريد الخاص بك',
    step2: 'يسجّل المستخدمون ويقدمون طعونهم',
    step3: 'اكسب عمولة على كل طعن مدفوع',
    step4: 'اطلب الصرف عند بلوغ الحد الأدنى',
    noActivity: 'لا يوجد نشاط إحالة بعد',
    submitAppeal: 'تقديم طعن', submitTitle: 'تقديم طعن نيابة عن العميل',
    submitDesc: 'قدّم طعناً مباشرة نيابة عن عملائك المُحالين',
    fullName: 'الاسم الكامل', email: 'البريد الإلكتروني', platformLabel: 'المنصة', issueType: 'نوع المشكلة',
    description: 'الوصف', country: 'الدولة', submit: 'إرسال الطعن',
    mySubmissions: 'طعوناتي', overview: 'نظرة عامة',
    commissionRate: 'نسبة العمولة',
  },
  de: {
    title: 'Affiliate-Dashboard', subtitle: 'Verdienen Sie Provisionen durch die Empfehlung von Nutzern',
    refLink: 'Ihr Empfehlungslink', copy: 'Kopieren', copied: 'Kopiert!',
    stats: 'Ihre Statistiken', referredUsers: 'Empfohlene Nutzer', paidAppeals: 'Bezahlte Beschwerden',
    pendingEarnings: 'Ausstehende Einnahmen', totalPaid: 'Gesamte Auszahlung',
    requestPayout: 'Auszahlung anfordern', payoutNote: 'Mindestbetrag',
    referredTable: 'Empfehlungsaktivität', date: 'Datum', platform: 'Plattform', status: 'Status', commission: 'Provision',
    howItWorks: 'So funktioniert es',
    step1: 'Teilen Sie Ihren Empfehlungslink', step2: 'Nutzer registrieren sich',
    step3: 'Verdienen Sie Provision pro Beschwerde', step4: 'Auszahlung anfordern',
    noActivity: 'Noch keine Empfehlungsaktivität',
    submitAppeal: 'Beschwerde einreichen', submitTitle: 'Beschwerde für Kunden einreichen',
    submitDesc: 'Reichen Sie eine Beschwerde direkt im Namen Ihrer empfohlenen Kunden ein',
    fullName: 'Vollständiger Name', email: 'E-Mail', platformLabel: 'Plattform', issueType: 'Problemtyp',
    description: 'Beschreibung', country: 'Land', submit: 'Einreichen',
    mySubmissions: 'Meine Einreichungen', overview: 'Übersicht',
    commissionRate: 'Provisionssatz',
  },
};

const PLATFORMS = ['Meta/Facebook', 'Instagram', 'Google/YouTube', 'TikTok', 'Twitter/X', 'Amazon', 'Apple', 'Microsoft', 'Snapchat', 'Spotify', 'Other'];

export default function AffiliateDashboard() {
  const { theme } = useTheme();
  const isDark = theme !== 'aurora';
  const { i18n } = useTranslation();
  const lang = ['en','ar','de'].includes(i18n.language) ? i18n.language : 'en';
  const s = strings[lang];

  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', platform: '', issue_type: '', description: '', country: '' });

  const card = isDark ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border border-white/[0.05]' : 'bg-white border border-gray-200 shadow-sm';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-600';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';
  const borderCl = isDark ? 'border-white/[0.05]' : 'border-gray-100';
  const rowHover = isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50';
  const inputCl = isDark ? 'bg-white/[0.04] border-white/[0.08] text-white placeholder-white/30' : 'bg-white border-gray-200 text-gray-900';

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const res = await affiliateAPI.stats();
      setStats(res.data);
    } catch { toast.error('Failed to load affiliate stats'); }
    finally { setLoading(false); }
  };

  const handleCopy = () => {
    if (!stats) return;
    navigator.clipboard.writeText(stats.referral_link).then(() => {
      setCopied(true);
      toast.success(s.copied);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSubmitAppeal = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.platform || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await appealsAPI.create(form);
      toast.success(`Appeal submitted! Tracking code: ${res.data.tracking_code}`);
      setForm({ full_name: '', email: '', platform: '', issue_type: '', description: '', country: '' });
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit appeal');
    } finally { setSubmitting(false); }
  };

  const canRequestPayout = stats && stats.pending_earnings >= stats.min_payout;

  const statCards = stats ? [
    { icon: Users, label: s.referredUsers, value: stats.referred_users, color: 'text-blue-500', bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50' },
    { icon: TrendingUp, label: s.paidAppeals, value: stats.paid_appeals, color: 'text-purple-500', bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50' },
    { icon: DollarSign, label: s.pendingEarnings, value: `$${stats.pending_earnings.toFixed(2)}`, color: 'text-amber-500', bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50' },
    { icon: Gift, label: s.totalPaid, value: `$${stats.total_paid.toFixed(2)}`, color: 'text-emerald-500', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50' },
  ] : [];

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors py-10 px-6 ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className={`text-3xl font-bold tracking-tight mb-2 ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.title}</h1>
          <p className={textS}>{s.subtitle}</p>
        </motion.div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className={`mb-6 ${isDark ? 'bg-white/[0.04] border border-white/[0.06]' : 'bg-gray-100'}`}>
            <TabsTrigger value="overview" className={isDark ? 'data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50' : ''}>{s.overview}</TabsTrigger>
            <TabsTrigger value="submit" className={isDark ? 'data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50' : ''}>
              <Plus className="w-4 h-4 mr-1" /> {s.submitAppeal}
            </TabsTrigger>
            <TabsTrigger value="activity" className={isDark ? 'data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50' : ''}>{s.referredTable}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {/* Referral Link */}
            {stats && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`${card} rounded-2xl p-6 mb-6`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`text-sm font-medium ${textS}`}>{s.refLink}</div>
                  <Badge className={isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200'}>
                    <Percent className="w-3 h-3 mr-1" /> {stats.commission_per_appeal}% {s.commissionRate}
                  </Badge>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`flex-1 px-4 py-3 rounded-xl text-sm font-mono truncate ${isDark ? 'bg-white/[0.04] border border-white/[0.06] text-blue-400' : 'bg-blue-50 border border-blue-100 text-blue-700'}`}>
                    {stats.referral_link}
                  </div>
                  <Button onClick={handleCopy} className={`flex-shrink-0 rounded-xl ${copied ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-blue-500 hover:bg-blue-600'} text-white`}>
                    {copied ? <><Check className="w-4 h-4 mr-2" />{s.copied}</> : <><Copy className="w-4 h-4 mr-2" />{s.copy}</>}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {statCards.map((sc, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.05 }} className={`${card} rounded-2xl p-5`}>
                  <div className={`w-9 h-9 ${sc.bg} rounded-xl flex items-center justify-center mb-3`}>
                    <sc.icon className={`w-5 h-5 ${sc.color}`} />
                  </div>
                  <div className={`text-2xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>{sc.value}</div>
                  <div className={`text-xs mt-1 ${textM}`}>{sc.label}</div>
                </motion.div>
              ))}
            </div>

            {/* Payout */}
            {stats && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={`${card} rounded-2xl p-6 mb-6`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`font-semibold ${textP}`}>{s.requestPayout}</div>
                    <div className={`text-sm mt-1 ${textM}`}>{s.payoutNote}: ${stats.min_payout}</div>
                  </div>
                  <Button disabled={!canRequestPayout} className={`rounded-xl ${canRequestPayout ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : isDark ? 'bg-white/[0.05] text-white/30 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
                    <DollarSign className="w-4 h-4 mr-2" /> {s.requestPayout} ${stats.pending_earnings.toFixed(2)}
                  </Button>
                </div>
                {stats.pending_earnings > 0 && (
                  <div className={`mt-3 rounded-lg overflow-hidden h-2 ${isDark ? 'bg-white/[0.05]' : 'bg-gray-100'}`}>
                    <div className="h-full bg-blue-500 rounded-lg transition-all" style={{ width: `${Math.min(100, (stats.pending_earnings / stats.min_payout) * 100)}%` }} />
                  </div>
                )}
              </motion.div>
            )}

            {/* How it works */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className={`${card} rounded-2xl p-6`}>
              <h3 className={`font-semibold mb-5 ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.howItWorks}</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[s.step1, s.step2, s.step3, s.step4].map((step, i) => (
                  <div key={i} className={`flex items-start gap-3 p-4 rounded-xl ${isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>{i + 1}</div>
                    <div className={`text-sm ${textS}`}>{step}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </TabsContent>

          <TabsContent value="submit">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`${card} rounded-2xl p-6`}>
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <Send className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h2 className={`text-lg font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.submitTitle}</h2>
                  <p className={`text-sm ${textM}`}>{s.submitDesc}</p>
                </div>
              </div>

              <form onSubmit={handleSubmitAppeal} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-medium mb-1.5 block ${textS}`}>{s.fullName} *</label>
                    <Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className={`rounded-xl ${inputCl}`} placeholder="John Doe" />
                  </div>
                  <div>
                    <label className={`text-sm font-medium mb-1.5 block ${textS}`}>{s.email} *</label>
                    <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={`rounded-xl ${inputCl}`} placeholder="john@example.com" />
                  </div>
                  <div>
                    <label className={`text-sm font-medium mb-1.5 block ${textS}`}>{s.platformLabel} *</label>
                    <Select value={form.platform} onValueChange={v => setForm({...form, platform: v})}>
                      <SelectTrigger className={`rounded-xl ${inputCl}`}><SelectValue placeholder="Select platform" /></SelectTrigger>
                      <SelectContent className={isDark ? 'bg-[#0A0A0C] border-white/10' : ''}>
                        {PLATFORMS.map(p => <SelectItem key={p} value={p} className={isDark ? 'text-white hover:bg-white/5' : ''}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={`text-sm font-medium mb-1.5 block ${textS}`}>{s.issueType}</label>
                    <Input value={form.issue_type} onChange={e => setForm({...form, issue_type: e.target.value})} className={`rounded-xl ${inputCl}`} placeholder="Account Banned, Content Removed..." />
                  </div>
                  <div>
                    <label className={`text-sm font-medium mb-1.5 block ${textS}`}>{s.country}</label>
                    <Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} className={`rounded-xl ${inputCl}`} placeholder="Germany" />
                  </div>
                </div>
                <div>
                  <label className={`text-sm font-medium mb-1.5 block ${textS}`}>{s.description} *</label>
                  <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className={`rounded-xl min-h-[120px] ${inputCl}`} placeholder="Describe the issue in detail..." />
                </div>
                <Button type="submit" disabled={submitting} className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl px-8">
                  {submitting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  {s.submit}
                </Button>
              </form>
            </motion.div>
          </TabsContent>

          <TabsContent value="activity">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`${card} rounded-2xl overflow-hidden`}>
              <div className={`p-5 border-b ${borderCl}`}>
                <h3 className={`font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.referredTable}</h3>
              </div>
              {stats && stats.referred_appeals && stats.referred_appeals.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${borderCl}`}>
                        {[s.date, s.platform, s.status, s.commission].map(h => (
                          <th key={h} className={`text-left text-xs font-medium p-4 ${textM}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {stats.referred_appeals.map((a, i) => (
                        <tr key={a.id || i} className={`border-b ${borderCl} ${rowHover} transition-colors`}>
                          <td className={`p-4 text-xs ${textS}`}>{new Date(a.created_at).toLocaleDateString()}</td>
                          <td className={`p-4 text-sm ${textP}`}>{a.platform || '—'}</td>
                          <td className="p-4">
                            <Badge className={`text-xs ${
                              a.status === 'approved' || a.status === 'completed'
                                ? isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                : isDark ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-blue-50 text-blue-600 border-blue-200'
                            }`}>{a.status}</Badge>
                          </td>
                          <td className={`p-4 text-sm font-medium ${['submitted','in_review','approved','completed'].includes(a.status) ? 'text-emerald-500' : textM}`}>
                            {['submitted','in_review','approved','completed'].includes(a.status) ? `+${stats.commission_per_appeal}%` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={`p-12 text-center ${textM}`}>
                  <FileText className={`w-12 h-12 mx-auto mb-3 ${textM}`} />
                  {s.noActivity}
                </div>
              )}
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
